import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { BoardProductScope } from './ProductService';

import type { Json } from '@/integrations/supabase/types';
import { resolveConnectionState } from '@/services/socialAuth/connection-state';

export interface BrandKitStore {
  myshopifyDomain: string;
  name: string | null;
}

export interface BrandKitStoreBinding {
  ownerId: string;
  brandKitId: string;
  myshopifyDomain: string;
  createdAt: string | null;
}

export type StoreBindingRemediationState = 'not_connected' | 'bound' | 'unbound' | 'ambiguous';

export interface StoreBindingRemediation {
  state: StoreBindingRemediationState;
  boundStores: BrandKitStore[];
  unboundStores: BrandKitStore[];
}

type JsonRecord = Record<string, Json | undefined>;

interface ShopifyConnectionRow {
  platform_user_id: string | null;
  is_active: boolean | null;
  updated_at: string | null;
  metadata: Json | null;
  connection_data: Json | null;
}

function normalizeDomain(value: string): string {
  return value.trim().toLowerCase();
}

class BrandKitStoreBindingService extends BaseService {
  /**
   * The live Shopify stores connected to this workspace. Reads `user_connections`
   * — the only table `connect-shopify` writes to (`direct_shopify_connections` is
   * a legacy table named `legacyTable` in `token-migration-utils.ts`; it holds 2
   * rows total, both from 2025-07-11, and nothing writes new ones). Rows are
   * grouped by shop domain and resolved with the same active-vs-superseded logic
   * `ProductService.getStoreConnection` uses via `resolveConnectionState`, because
   * a reconnect leaves the prior row behind instead of replacing it.
   */
  private async resolveActiveShopifyStores(scope: BoardProductScope): Promise<BrandKitStore[]> {
    const { data, error } = await this.client
      .from('user_connections')
      .select('platform_user_id, is_active, updated_at, metadata, connection_data')
      .eq('user_id', scope.ownerId)
      .eq('brand_kit_id', scope.brandKitId)
      .eq('platform', 'shopify');
    if (error) throw error;

    const byDomain = new Map<string, ShopifyConnectionRow[]>();
    for (const row of (data ?? []) as ShopifyConnectionRow[]) {
      const domain = normalizeDomain(String(row.platform_user_id ?? ''));
      if (!domain) continue;
      const bucket = byDomain.get(domain);
      if (bucket) bucket.push(row);
      else byDomain.set(domain, [row]);
    }

    const stores: BrandKitStore[] = [];
    for (const [domain, rows] of byDomain) {
      const resolved = resolveConnectionState(rows);
      if (resolved.state !== 'active' || !resolved.row) continue;
      const metadata = (resolved.row.metadata as JsonRecord | null) ?? {};
      const connectionData = (resolved.row.connection_data as JsonRecord | null) ?? {};
      const name =
        (typeof metadata.shop_name === 'string' ? metadata.shop_name : null) ||
        (typeof connectionData.shop_name === 'string' ? connectionData.shop_name : null);
      stores.push({ myshopifyDomain: domain, name });
    }
    return stores;
  }

  async getRemediationState(
    scope: BoardProductScope,
  ): Promise<ServiceResponse<StoreBindingRemediation>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const [stores, bindingResult] = await Promise.all([
        this.resolveActiveShopifyStores(scope),
        this.client
          .from('brand_kit_store_bindings')
          .select('owner_id, brand_kit_id, myshopify_domain, created_at')
          .eq('owner_id', scope.ownerId)
          .eq('brand_kit_id', scope.brandKitId),
      ]);

      if (bindingResult.error) throw bindingResult.error;

      const boundDomains = new Set(
        ((bindingResult.data ?? []) as Array<Record<string, unknown>>).map((row) =>
          normalizeDomain(String(row.myshopify_domain ?? '')),
        ),
      );
      const boundStores = stores.filter((store) => boundDomains.has(store.myshopifyDomain));
      const unboundStores = stores.filter((store) => !boundDomains.has(store.myshopifyDomain));

      const state: StoreBindingRemediationState =
        stores.length === 0
          ? 'not_connected'
          : boundStores.length > 0 && unboundStores.length === 0
            ? 'bound'
            : boundStores.length === 0 && unboundStores.length > 1
              ? 'ambiguous'
              : 'unbound';

      return { state, boundStores, unboundStores };
    });
  }

  async bindStore(
    scope: BoardProductScope,
    myshopifyDomain: string,
  ): Promise<ServiceResponse<BrandKitStoreBinding>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const domain = normalizeDomain(myshopifyDomain);
      if (!domain) throw new Error('A Shopify store domain is required');

      // Validate the store before attempting the write. The composite FK and
      // RLS repeat this invariant at the database boundary, including for
      // service-role callers that bypass browser-side checks.
      const stores = await this.resolveActiveShopifyStores(scope);
      const store = stores.find((candidate) => candidate.myshopifyDomain === domain);
      if (!store) throw new Error(`Store ${domain} does not belong to this account`);

      const { data, error } = await this.client
        .from('brand_kit_store_bindings')
        .insert({
          owner_id: scope.ownerId,
          brand_kit_id: scope.brandKitId,
          myshopify_domain: domain,
        })
        .select('owner_id, brand_kit_id, myshopify_domain, created_at')
        .single();
      if (error) throw error;
      return {
        ownerId: data.owner_id,
        brandKitId: data.brand_kit_id,
        myshopifyDomain: data.myshopify_domain,
        createdAt: data.created_at ?? null,
      };
    });
  }

  async unbindStore(
    scope: BoardProductScope,
    myshopifyDomain: string,
  ): Promise<ServiceResponse<null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { error } = await this.client
        .from('brand_kit_store_bindings')
        .delete()
        .eq('owner_id', scope.ownerId)
        .eq('brand_kit_id', scope.brandKitId)
        .eq('myshopify_domain', normalizeDomain(myshopifyDomain));
      if (error) throw error;
      return null;
    });
  }
}

export const brandKitStoreBindingService = new BrandKitStoreBindingService();
